/**
 *
 *  Become a Member page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 17/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "react-modal";
import Cookies from "js-cookie";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";
import { useRegion } from "../../context/RegionProvider";

// Components
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import SignUpForm from "./SignUpForm/SignUpForm";
import CodeVerification from "../SignUp/CodeVerification/CodeVerification";
import Registration from "../SignUp/Registration/Registration";
import PasswordConfirmForm from "../SignUp/PasswordConfirmForm/PasswordConfirmForm";
import SetEmail from "../SignUp/SetEmail/SetEmail";
import SetPhoneNumber from "../SignUp/SetPhoneNumber/SetPhoneNumber";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import TrainerRating from "../TrainerRating/TrainerRating";
import Button from "../Button/Button";
import TBKTooltip from "../TruBeTooltip/TruBeTooltip";
// import { EventCard } from "../LifestyleLP/LifestyleLP";
// import UpcomingExperiencesSlider from "../LifestyleLP/UpcomingExperiencesSlider/UpcomingExperiencesSlider";
// import AppBanner from "../AppBanner/AppBanner";
// import AppBannerUK from "../UKComponents/AppBanner/AppBanner";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import BlockerIcon from "../../icons/BlockerIcon";
import CopyIcon from "../../icons/CopyIcon";

// Utils
import copy from "../../utils/copy";
import { clientWidth, makeCancelable, generatePassword } from "../../utils";
import { modalStyles } from "../../constants";

// Images
import Arrow from "./Arrow";

import becomeAMemberBig from "../../images/become-a-member-big.jpg";
import becomeAMemberBig2x from "../../images/become-a-member-big@2x.jpg";
import becomeAMemberHeroMobile from "../../images/become-a-member-hero-mobile.jpg";
import becomeAMemberHeroMobile2x from "../../images/become-a-member-hero-mobile@2x.jpg";

import HeartIcon from "../../icons/HeartIcon";
import HighQualityIcon from "../../icons/HighQualityIcon";
import OneOnOneSessionPriceIcon from "../../icons/OneOnOneSessionPriceIcon";
import VarietyOfDisciplinesIcon from "../../icons/VarietyOfDisciplinesIcon";

import properHotel from "./proper-hotel.jpg";
import properHotel2x from "./proper-hotel@2x.jpg";
import ProperLogo from "../../icons/ProperLogo";

const AUTOHIDE_TIME = 3000;

const sliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
];

const TrainerCard = ({
  trainerPictureUrl,
  trainerPictureSrcSet,
  trainerName,
  headline,
  productName,
}) => {
  return (
    <div
      className="tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-shadow-trube"
      // onClick={handleCardClick}
    >
      <div className="tbk-relative tbk-flex">
        <div className="tbk-h-36 tbk-shrink-0 tbk-grow-0 tbk-basis-36">
          <img
            className="tb-profile-picture-img img-responsive tbk-h-full tbk-object-cover"
            itemProp="image"
            src={trainerPictureUrl}
            srcSet={trainerPictureSrcSet}
            alt="Coach profile"
          />
        </div>
        <div
          className="tbk-w-full tbk-p-2"
          style={{
            minWidth: 0,
          }}
        >
          <h4
            itemProp="name"
            className={
              "tbk-text-title-bold tbk-flex tbk-items-center tbk-text-primary"
            }
          >
            {trainerName}
            <TrainerRating
              className="tbk-ml-0.5 tbk-flex tbk-items-center"
              averageRatingClassName="tbk-text-coral tbk-text-secondary"
              partner={{
                partnerRatingRecap: {
                  average: 5,
                  numberOfRatings: 0,
                },
              }}
            />
          </h4>
          <div className="tbk-flex tbk-items-end">
            <div className="flex-grow-1">
              <div className="tbk-text-small tbk-max-h-[72px] tbk-overflow-hidden tbk-text-ellipsis tbk-text-primary">
                {productName}
                <br />
                {headline}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgressIndicator = ({ level }) => {
  return (
    <div className="tbk-mb-5 tbk-flex tbk-items-center tbk-justify-center tbk-pb-1">
      {level > 1 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div className="tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-bg-blue tbk-text-center tbk-text-basic-white">
          <span className="tbk-text-small-bold tbk-w-full tbk-text-basic-white">
            1
          </span>
        </div>
      )}

      {/*<CheckOIcon />*/}
      {/*<CheckNiceIcon />*/}
      <Arrow className="tbk-mr-1" />
      {level > 2 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div
          className={`tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-border tbk-border-solid tbk-border-blue tbk-text-center ${
            level > 1 ? "tbk-bg-blue" : "tbk-bg-basic-white"
          }`}
        >
          <span
            className={`tbk-text-small-bold tbk-w-full ${
              level > 1 ? "tbk-text-basic-white" : "tbk-text-blue"
            }`}
          >
            2
          </span>
        </div>
      )}
      <Arrow className="tbk-mr-1" />
      {level > 3 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div
          className={`tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-border tbk-border-solid tbk-border-blue tbk-text-center ${
            level > 2 ? "tbk-bg-blue" : "tbk-bg-basic-white"
          }`}
        >
          <span
            className={`tbk-text-small-bold tbk-w-full ${
              level > 2 ? "tbk-text-basic-white" : "tbk-text-blue"
            }`}
          >
            3
          </span>
        </div>
      )}
    </div>
  );
};

const isLoginPath = (pathname) => {
  return pathname.includes("/login") || pathname.includes("/become-a-member");
};

export default function BecomeMemberPage() {
  const history = useHistory();
  const location = useLocation();

  const { theme } = useTheme();
  const { region } = useRegion();

  const {
    user,
    referralCode,
    migrationCode,
    registrationCode,
    upcomingEvents,
    applyReferral,
    setReferralCode,
    setMigrationCode,
    loadUpcomingEvents,
    signUp,
    signUpWithApple,
    signUpWithGoogle,
    signUpWithFacebook,
    checkEmail,
    verifyPhoneNumber,
    verifyPhoneNumberOrEmailForSimpleLogin,
    sendEmailCode,
    clearUpcomingEventsLoaded,
  } = useAppState();
  const {
    authState,
    isAppleSignIn,
    identityToken,
    authorizationCode,
    accessToken, // For Facebook login
    authVendor,
    userPhoneCode: phoneCode,
    userPhoneNumber: phoneNumber,
    userPassword,
    userFirstName: firstName,
    userFamilyName: familyName,
    userEmail,
    setUserPhoneCode,
    setUserPhoneNumber,
    setAuthVendor,
    setHasAuthError,
  } = useSignUpState();

  const [_authState, setAuthState] = useState("");
  const [returnTo, setReturnTo] = useState("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [originPage, setOriginPage] = useState(null);
  const [isCopyTooltipShow, setIsCopyTooltipShow] = useState(false);
  const [ctaText, setCtaText] = useState("Finish registration");

  const heroRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);
  const copyButtonRef = React.useRef(null);
  const hideCancelablePromiseRef = React.useRef(null);

  useEffect(
    () => {
      setHasAuthError(false);

      loadUpcomingEvents(
        undefined,
        "UPCOMING,STARTED,UPCOMING_FULL",
        { sort: "start", page: 0, size: 8 },
        user ? user.region : region
      );

      return () => {
        clearUpcomingEventsLoaded();
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setOriginPage(location.state && location.state.from);
  }, [location.state]);

  const membershipJoinCode = "TBHOL30"; //process.env.REACT_APP_MEMBERSHIP_JOIN_CODE; //"TBLIFE";

  // TODO write unit tests!
  const fromLifestylePage =
    originPage &&
    (typeof originPage === "string"
      ? originPage.includes("lifestyle") || originPage.includes("events")
      : originPage &&
        originPage.pathname &&
        (originPage.pathname.includes("lifestyle") ||
          originPage.pathname.includes("events")));

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get("return_to");

    setReturnTo(returnTo);

    // const urlParams = new URLSearchParams(location.search);
    // const authStateQueryString = urlParams.get("authState");
    //
    // if (authStateQueryString) {
    //   urlParams.delete("authState");
    // } else if (authState === "login") {
    //   history.push({
    //     pathname: "/login",
    //     state: location.state,
    //     search: urlParams.toString(),
    //   });
    // }
  }, []);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const redirectToPreviousUrl = useCallback(
    (userInfo) => {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get("return_to");

      if (returnTo) {
        urlParams.delete("return_to");
        urlParams.delete("authState");

        const returnUrl = new URL(
          decodeURIComponent(returnTo),
          window.location.origin
        );
        const pathname = returnUrl.pathname;
        const search = returnUrl.search;

        if (userInfo && !userInfo.questionnaireCompleted) {
          history.replace({
            pathname: "/questionnaire",
            state: location.state,
            search: search
              ? `${search}${
                  urlParams.size > 0 ? `&${urlParams.toString()}` : ""
                }`
              : urlParams.size > 0
              ? `?${urlParams.toString()}`
              : "",
          });
        } else {
          history.replace({
            pathname,
            state: location.state,
            search: search
              ? `${search}${
                  urlParams.size > 0 ? `&${urlParams.toString()}` : ""
                }`
              : urlParams.size > 0
              ? `?${urlParams.toString()}`
              : "",
          });
        }
      } else {
        const stateFrom = location && location.state && location.state.from;

        let returnToUrl =
          typeof stateFrom === "object"
            ? `${stateFrom.pathname}${stateFrom.search}`
            : stateFrom;

        if (returnToUrl && returnToUrl.includes("/login")) {
          returnToUrl = "";
        }

        if (userInfo && !userInfo.questionnaireCompleted) {
          if (returnToUrl) {
            history.replace({
              pathname: "/questionnaire",
              search: `?return_to=${returnToUrl}`,
            });
          } else {
            history.replace("/questionnaire");
          }
        } else {
          history.replace(
            returnToUrl || {
              pathname: "/partners",
              search: window.location.search,
            }
          );
        }
      }
    },
    [history, location, returnTo]
  );

  const handleCopyToClipboard = useCallback(
    (e) => {
      if (copy(membershipJoinCode)) {
        showCopyTooltip();
      }

      // Analytics.trackEvent(SHARE_CATEGORY, '', target.tagName.toLocaleLowerCase() === 'button' ? EVENT_SHARE_LINK : EVENT_SHARE_CLIPBOARD);
    },
    [membershipJoinCode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      return;
    }

    // Auto hide afer 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      setIsCopyTooltipShow(true);

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    hideCancelablePromiseRef.current = makeCancelable(hidePromise);

    return hideCancelablePromiseRef.current.promise.then(() => {
      hideCancelablePromiseRef.current = null;
      hideCopyTooltip();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      hideCancelablePromiseRef.current.cancel();
      hideCancelablePromiseRef.current = null;
    }

    setIsCopyTooltipShow(false);
  }, []);

  const onSignUp = useCallback(
    (userInfo) => {
      if (!userInfo) {
        return;
      }

      if (referralCode && false) {
        applyReferral(userInfo.id, referralCode)
          .then((res) => {
            // Referral code is valid
            return openInfoModal(
              `You received ${res.data.symbol}${res.data.receiverReward} referral credits.`,
              "Referral Bonus Applied"
            );
          })
          .catch((res) => {
            return openInfoModal(res.message, "Link Error");
          })
          .then(() => {
            redirectToPreviousUrl(userInfo);
          })
          .finally(() => {
            // Regardless the referral code is valid or not we don't need it anymore
            // (so it won't be applied on re-login for example)
            setReferralCode("");

            Cookies.remove("referral");
          });
      } else {
        redirectToPreviousUrl(userInfo);
      }
    },
    [history, location, referralCode]
  );

  const onSignIn = (userInfo) => {
    if (!userInfo) {
      return;
    }

    redirectToPreviousUrl(userInfo);
  };

  const redirectToLogin = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("authState", "loginWithEmail");

    history.push({
      pathname: "/login",
      state: location.state,
      search: urlParams.toString(),
    });
  }, [location]);

  const handleSignIn = (userInfo, returnTo, applelogin) => {
    let returnToUrl;
    let stateFrom;

    if (applelogin) {
      returnToUrl = window.localStorage.getItem("apple-redirect-return-to");
      stateFrom = window.localStorage.getItem("apple-redirect-state-from");

      window.localStorage.removeItem("apple-redirect-return-to");
      window.localStorage.removeItem("apple-redirect-state-from");
    }

    returnToUrl = returnToUrl || returnTo;

    if (typeof returnToUrl !== "string") {
      returnToUrl = returnToUrl && returnToUrl.pathname;
    }

    // FIXME quick fix to prevent to return to the login page again
    if (returnToUrl && !isLoginPath(returnToUrl)) {
      const query = new URLSearchParams(returnToUrl.split("?")[1]);

      // if (isLoginPath(returnToUrl)) {
      //   returnToUrl = "/partners";
      // }

      if (returnToUrl.includes("/partners")) {
        query.append("apple_login", "yes");
      }

      if (
        userInfo &&
        userInfo.customer &&
        !userInfo.customer.questionnaireCompleted
      ) {
        history.push("/questionnaire");
      } else {
        history.replace({
          pathname: returnToUrl.split("?")[0],
          state: { from: stateFrom },
          search: query.toString(),
        });
      }
      //history.replace(returnToUrl);
    } else {
      if (!applelogin) {
        stateFrom = location && location.state && location.state.from;
        // } else if (stateFrom && stateFrom === "/lifestyle") {
        // setPromoCode("TBHOL30");
      }

      // FIXME make more robust, quick fix for now
      if (stateFrom && isLoginPath(stateFrom)) {
        stateFrom = undefined;
      }

      // On login `state.from` is populated, so we preserve search params that way.
      if (
        userInfo &&
        userInfo.customer &&
        !userInfo.customer.questionnaireCompleted
      ) {
        history.push("/questionnaire");
      } else {
        history.replace(
          stateFrom || {
            pathname: "/partners",
            // search: window.location.search,
            search: applelogin ? "?apple_login=yes" : "",
          }
        );
      }
    }
    // window.location.href = ;
  };

  const handleSignUp = useCallback(
    ({ email, password, code, onNext }) => {
      // email = email || userEmail;
      password = password || userPassword;
      code = code || registrationCode;

      if (email && (!code || authState === "loginWithEmail")) {
        checkEmail(email)
          .then((res) => {
            if (res.status === "MIGRATION_CUSTOMER" && res.migrationData) {
              const { phoneCode, phoneNumber } = res.migrationData;

              setUserPhoneCode(phoneCode);
              setUserPhoneNumber(phoneNumber);

              setAuthState("codeVerificationEmailSignUp");
              setAuthVendor("");

              return sendEmailCode(email);
              // return verifyPhoneNumber({
              //   phoneCode,
              //   phoneNumber,
              //   authVendor: "",
              // });
            } else if (res.status === "EXISTING_CUSTOMER") {
              return verifyPhoneNumberOrEmailForSimpleLogin({ email });
            } else if (res.status === "NEW_CUSTOMER") {
              setAuthState("setPhoneNumber");
              setAuthVendor("");
            }

            return res;
          })
          .then((res) => {
            if (res.verified) {
              redirectToLogin();
            }

            if (res.status || res.verified) {
              onNext && onNext(res);
            }

            // if (res.verified) {
            //   setAuthState("passwordSet");
            // }
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } // {
      else if (identityToken && authorizationCode) {
        signUpWithApple(
          firstName,
          familyName,
          generatePassword(12), //password,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          identityToken,
          authorizationCode,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else if (identityToken && authVendor === "google") {
        signUpWithGoogle(
          firstName,
          familyName,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          identityToken,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else if (accessToken && authVendor === "facebook") {
        signUpWithFacebook(
          firstName,
          familyName,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          accessToken,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else {
        signUp(
          firstName,
          familyName,
          generatePassword(12), // password,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      }
    },
    [
      authVendor,
      authState,
      registrationCode,
      identityToken,
      authorizationCode,
      phoneCode,
      phoneNumber,
      userPassword,
      firstName,
      familyName,
      userEmail,
      redirectToLogin,
      region,
    ]
  );

  const handleResetPasswordClick = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("authState", "passwordResetEmail");

    history.push({
      pathname: "/login",
      state: location.state,
      search: urlParams.toString(),
    });
  }, [history]);

  const handleNext = useCallback(
    (authVendor, res) => {
      if (res && res.customer) {
        onSignIn(res.customer);

        return;
      }

      // Migrated user verified phone number
      if (res.registrationCode && (res.migrationCode || migrationCode)) {
        handleSignUp({ code: res.registrationCode });

        return;
      }

      if (
        res.registrationCode ||
        (isAppleSignIn && authVendor !== "" && authVendor === undefined) ||
        authVendor === "google" ||
        authVendor === "facebook"
      ) {
        //? // ? userFirstName && userFamilyName ?  :
        setAuthState("setFullName");
      } else if (res.migrationCode) {
        // Migrated user verified email
        setAuthState("setPhoneNumber" /*"setPhoneNumberMigration"*/);

        // return;
        // } else {
        //   setAuthState("passwordSet");
      }
    },
    [isAppleSignIn, migrationCode, handleSignUp]
  );

  return (
    <div className="tbk-flex tbk-h-full tbk-flex-col tbk-items-stretch lg:tbk-grid lg:tbk-grid-rows-[64px_1fr_auto]">
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content="TruBe connects you with the best fitness & wellbeing trainers, specialising in one-to-one sessions for Pilates, Yoga, Personal Training, HIIT, Running, and more"
      />
      <Header
        behaviour="none"
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
        showLinks={false}
        showMenu={false}
      />
      {!_authState ? (
        <div ref={heroRef} className="tbk-relative tbk-bg-grey-light !tbk-pt-0">
          <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-left-0 !tbk-hidden tbk-hidden tbk-h-74 tbk-w-full tbk-overflow-hidden tbk-bg-basic-trueblack md:tbk-h-full lg:tbk-bottom-auto lg:tbk-top-0 lg:tbk-block">
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={`${becomeAMemberBig} 1x, ${becomeAMemberBig2x} 2x,`}
              />
              <source
                srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
              />
              <img
                className="tbk-h-full tbk-w-full tbk-grow tbk-object-cover"
                src={becomeAMemberBig2x}
                srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                alt="Hero"
              />
            </picture>
          </div>
          <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-grow lg:tbk-px-2">
            <div className="md:tbk-pb-24_ tbk-relative tbk-flex tbk-h-full tbk-flex-col md:tbk-grid md:tbk-grid-cols-become-member-tablet-layout md:tbk-pb-6 lg:tbk-gap-6 lg:tbk-gap-8 xl:tbk-grid-cols-become-member-layout">
              <div className="md:tbk-mt-5">
                <div className="tbk-border-solid tbk-border-grey-inactive tbk-bg-basic-white tbk-p-3 md:tbk-rounded-2xl lg:tbk-border">
                  <SignUpForm
                    title={"Sign Up or Login"}
                    className=""
                    ctaText={"Continue"}
                    dividerText={"Or continue with"}
                    returnTo={
                      returnTo || (location.state && location.state.from)
                    }
                    onNext={(authVendor, params, isLogin, isEmailLogin) =>
                      authVendor === "google" || authVendor === "facebook"
                        ? setAuthState("setPhoneNumber")
                        : authVendor === "email"
                        ? (setAuthState("setEmail"), setCtaText("Continue"))
                        : setAuthState(
                            isLogin
                              ? "codeVerificationLogin"
                              : isEmailLogin
                              ? "codeVerificationEmail"
                              : "codeVerification"
                          )
                    }
                    onSignIn={handleSignIn}
                  />
                </div>
              </div>

              <div className="tbk-relative tbk-min-w-0 tbk-px-2 tbk-pt-4 tbk-pb-3 lg:tbk-h-auto lg:tbk-px-0 lg:tbk-pt-0 lg:tbk-pb-0">
                <div className="tbk-absolute tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-top-0 !tbk-hidden tbk-h-full tbk-w-full tbk-overflow-hidden tbk-bg-grey-light lg:tbk-hidden">
                  <picture>
                    <source
                      media="(min-width: 768px)"
                      srcSet={`${becomeAMemberBig} 1x, ${becomeAMemberBig2x} 2x,`}
                    />
                    <source
                      srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                    />
                    <img
                      className="tbk-h-full tbk-w-full tbk-grow tbk-object-cover"
                      src={becomeAMemberBig2x}
                      srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                      alt="Hero"
                    />
                  </picture>
                </div>
                <div className="tbk-relative lg:tbk-pt-5">
                  <div className="tbk-pb-3 tbk-text-center">
                    {/*<img className="tbk-inline-block tbk-mb-4" src={properHotelLogo} srcSet={`${properHotelLogo} 1x, ${properHotelLogo2x} 2x`} />*/}
                    <ProperLogo className="tbk-mb-4 tbk-inline-block" />
                    <h1 className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-1 tbk-mt-1 tbk-text-primary lg:tbk-mb-2 lg:tbk-mt-0 lg:!tbk-leading-tight">
                      Create an Account to Schedule Your{" "}
                      <span className="tbk-text-[#FF7557]_ tbk-text-blue">
                        Private Training Session
                      </span>{" "}
                      at
                      <br />
                      Santa Monica Proper.
                    </h1>
                    {/*<p className="tbk-text-main tbk-mb-4_ tbk-text-primary">
                      Currently available in{" "}
                      {region === "US" ? "Los Angeles, CA" : "London"} and
                      surrounding areas.
                      <br /> Launching in more cities soon...
                    </p>*/}
                  </div>

                  <div className="tbk-mb-6 tbk-grid tbk-grid-cols-2 tbk-justify-between tbk-gap-2 md:tbk-flex lg:tbk-mb-4 lg:tbk-justify-center">
                    <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-mr-2 lg:tbk-ml-0">
                      <HeartIcon className="tbk-mb-1 tbk-inline-block" />
                      <div className="tbk-text-small tbk-mx-auto tbk-w-38 !tbk-font-semibold md:tbk-px-1">
                        First Session
                        <br />
                        Complimentary for Proper Club Members
                      </div>
                    </div>
                    <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-mr-2 lg:tbk-ml-0">
                      <HighQualityIcon className="tbk-mb-1 tbk-inline-block" />
                      <div className="tbk-text-small tbk-mx-auto tbk-w-38 !tbk-font-semibold md:tbk-px-1">
                        Highly-Rated
                        <br />
                        Certified Coaches
                      </div>
                    </div>
                    <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary md:tbk-hidden lg:tbk-mr-2 lg:tbk-ml-0 xl:tbk-block ">
                      <VarietyOfDisciplinesIcon className="tbk-mb-1 tbk-inline-block" />
                      <div className="tbk-text-small tbk-mx-auto tbk-w-38 !tbk-font-semibold md:tbk-px-1">
                        Variety
                        <br />
                        of Disciplines
                      </div>
                    </div>
                    <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-mr-0 lg:tbk-ml-0">
                      <svg
                        className="tbk-mb-1 tbk-inline-block"
                        width="55"
                        height="32"
                        viewBox="0 0 55 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_25051_4769)">
                          <path
                            d="M34.3203 13.2778C34.3203 11.9523 34.6946 10.9387 35.4431 10.2369C36.1916 9.5352 37.1741 9.18433 38.3904 9.18433C39.5912 9.18433 40.5658 9.5352 41.3143 10.2369C42.0628 10.9387 42.4371 11.9523 42.4371 13.2778C42.4371 14.5877 42.0628 15.6013 41.3143 16.3187C40.5658 17.0204 39.5912 17.3713 38.3904 17.3713C37.1896 17.3713 36.2072 17.0204 35.4431 16.3187C34.6946 15.6013 34.3203 14.5877 34.3203 13.2778ZM50.9282 9.44163L42.0628 25.9793H37.6653L46.5306 9.44163H50.9282ZM38.367 11.8743C37.7276 11.8743 37.408 12.3422 37.408 13.2778C37.408 14.2135 37.7276 14.6813 38.367 14.6813C38.6789 14.6813 38.9206 14.5643 39.0921 14.3304C39.2793 14.0965 39.3728 13.7456 39.3728 13.2778C39.3728 12.81 39.2793 12.4591 39.0921 12.2252C38.9206 11.9913 38.6789 11.8743 38.367 11.8743ZM46.1563 22.1197C46.1563 20.8098 46.5306 19.804 47.2791 19.1022C48.0432 18.3849 49.0257 18.0262 50.2264 18.0262C51.4272 18.0262 52.394 18.3849 53.1269 19.1022C53.8755 19.804 54.2497 20.8098 54.2497 22.1197C54.2497 23.4452 53.8755 24.4667 53.1269 25.184C52.394 25.8857 51.4272 26.2366 50.2264 26.2366C49.0257 26.2366 48.0432 25.8857 47.2791 25.184C46.5306 24.4667 46.1563 23.4452 46.1563 22.1197ZM50.203 20.7163C49.5481 20.7163 49.2206 21.1841 49.2206 22.1197C49.2206 23.071 49.5481 23.5466 50.203 23.5466C50.4993 23.5466 50.7332 23.4296 50.9048 23.1957C51.0919 22.9462 51.1855 22.5876 51.1855 22.1197C51.1855 21.6675 51.0919 21.3244 50.9048 21.0905C50.7332 20.841 50.4993 20.7163 50.203 20.7163Z"
                            fill="black"
                          />
                          <path
                            d="M20.4209 17.1842C20.4209 14.502 20.9667 12.389 22.0583 10.8451C23.1499 9.3013 24.8653 8.52939 27.2044 8.52939C29.5435 8.52939 31.2589 9.3013 32.3505 10.8451C33.4577 12.389 34.0113 14.502 34.0113 17.1842C34.0113 19.8976 33.4655 22.0262 32.3739 23.57C31.2823 25.1138 29.5591 25.8858 27.2044 25.8858C24.8497 25.8858 23.1265 25.1138 22.0349 23.57C20.9589 22.0262 20.4209 19.8976 20.4209 17.1842ZM29.4733 17.1842C29.4733 15.7807 29.3252 14.7125 29.0289 13.9796C28.7326 13.231 28.1244 12.8568 27.2044 12.8568C26.2843 12.8568 25.6762 13.231 25.3799 13.9796C25.0836 14.7125 24.9354 15.7807 24.9354 17.1842C24.9354 18.6188 25.0758 19.7104 25.3565 20.459C25.6528 21.1919 26.2687 21.5584 27.2044 21.5584C28.14 21.5584 28.7482 21.1919 29.0289 20.459C29.3252 19.7104 29.4733 18.6188 29.4733 17.1842Z"
                            fill="black"
                          />
                          <path
                            d="M12.0527 13.1843V8.88025H19.2573V25.9793H14.462V13.1843H12.0527Z"
                            fill="black"
                          />
                          <path
                            d="M10.7169 15.9678V19.7806H0.120605V15.9678H10.7169Z"
                            fill="black"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_25051_4769">
                            <rect
                              width="54"
                              height="32"
                              fill="white"
                              transform="translate(0.25)"
                            />
                          </clipPath>
                        </defs>
                      </svg>

                      <div className="tbk-text-small tbk-mx-auto tbk-w-32 !tbk-font-semibold md:tbk-px-1">
                        10% Off
                        <br />
                        10 Sessions Pack
                      </div>
                    </div>
                  </div>

                  <div className="tbk-pb-3">
                    {/*<div className="tbk-relative tbk-mb-3 tbk-text-center lg:tbk-mb-5">
                        <h3 className="tbk-text-h3-subtitle _lg:tbk-text-title-bold tbk-inline-block tbk-bg-grey-light tbk-px-2 tbk-text-center tbk-text-blue-grey">
                          Upcoming Classes
                        </h3>
                        <hr className="tbk-mb-0 tbk--mt-1.5 tbk-border-blue-grey-light lg:tbk--mt-2" />
                      </div>*/}

                    <img
                      className="tbk-h-62 tbk-w-full tbk-rounded-2xl tbk-object-cover"
                      src={properHotel}
                      srcSet={`${properHotel} 1x, ${properHotel2x} 2x`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-7 lg:tbk-pt-7 lg:tbk-pb-8">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8 lg:tbk-pb-5">
            <div className="tbk-relative tbk-mx-auto tbk-mt-4 md:tbk-mt-0 md:tbk-w-108">
              {_authState === "setPhoneNumber" ? (
                <>
                  <ProgressIndicator level={1} />
                  <SetPhoneNumber
                    onNext={() => setAuthState("codeVerification")}
                  />
                </>
              ) : _authState === "codeVerification" ||
                _authState === "codeVerificationEmail" ||
                _authState === "codeVerificationEmailSignUp" ||
                _authState === "loginWithEmail" ||
                _authState === "codeVerificationLogin" ? (
                <>
                  <ProgressIndicator
                    level={
                      isAppleSignIn ||
                      authVendor === "google" ||
                      authVendor === "facebook"
                        ? 2
                        : 1
                    }
                  />
                  <CodeVerification
                    isLogin={
                      _authState === "loginWithEmail" ||
                      _authState === "codeVerificationLogin" ||
                      _authState === "codeVerificationEmail"
                    }
                    isSignUp={_authState === "codeVerificationEmailSignUp"} // is it enough??
                    withEmail={_authState === "codeVerificationEmail"}
                    onNext={handleNext}
                    onResetPasswordClick={handleResetPasswordClick}
                    onCreateNewAccountClick={() => setAuthState("")}
                    showIcon={false}
                  />
                </>
              ) : _authState === "passwordSet" ? (
                <>
                  <ProgressIndicator level={2} />
                  <PasswordConfirmForm
                    onNext={() => setAuthState("setFullName")}
                  />
                </>
              ) : _authState === "setFullName" ? (
                <>
                  <ProgressIndicator level={3} />
                  <Registration onNext={() => setAuthState("setEmail")} />
                </>
              ) : _authState === "setEmail" ? (
                <>
                  <ProgressIndicator level={ctaText === "Continue" ? 1 : 4} />
                  <SetEmail ctaText={ctaText} onNext={handleSignUp} />
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/*{!_authState ? region == "US" ? <AppBanner /> : <AppBannerUK /> : null}*/}
      <Footer />

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {infoModalTitle === "Referral Bonus Applied" ? (
            <CheckNiceIcon size={40} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <BlockerIcon size={40} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </div>
  );
}
